
import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Table, Button, Tag, Space, Modal, message, Skeleton, Card, Empty, Image } from "antd";
import { EditOutlined, DeleteOutlined, PlusOutlined, ExclamationCircleOutlined, EyeOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { eventApi, type Event } from '../../services/events';
import { hackathonApi } from '../../services/hackathon';
import Pagination from '../../components/Pagination';

const EventsList = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [activeHackathonId, setActiveHackathonId] = useState<string | null>(null);
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;

  // Fetch all hackathons to find the active one
  const { data: hackathonsResponse, isLoading: isLoadingHackathons } = useQuery({
    queryKey: ['hackathons'],
    queryFn: hackathonApi.getHackathons,
  });

  // Find active hackathon
  useEffect(() => {
    if (hackathonsResponse?.data) {
      const activeHackathon = hackathonsResponse.data.find(h => h.isActive === true);
      if (activeHackathon) {
        setActiveHackathonId(activeHackathon._id);
      }
    }
  }, [hackathonsResponse]);

  // Fetch events for active hackathon
  const { data: eventsResponse, isLoading: isLoadingEvents } = useQuery({
    queryKey: ['events', activeHackathonId],
    queryFn: () => eventApi.getEvents(activeHackathonId!),
    enabled: !!activeHackathonId,
  });

  // Delete event mutation
  const deleteEventMutation = useMutation({
    mutationFn: eventApi.deleteEvent,
    onSuccess: () => {
      message.success('Event deleted successfully');
      queryClient.invalidateQueries({ queryKey: ['events', activeHackathonId] });
    },
    onError: () => {
      message.error('Failed to delete event');
    },
  });

  const handleAddEvent = () => {
    navigate('/events/add');
  };

  const handleEditEvent = (eventId: string) => {
    navigate(`/events/edit/${eventId}`);
  };

  const handleDeleteEvent = (event: Event) => {
    Modal.confirm({
      title: 'Are you sure you want to delete this event?',
      icon: <ExclamationCircleOutlined />,
      content: `This will permanently delete "${event.title}". This action cannot be undone.`,
      okText: 'Yes, Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk() {
        deleteEventMutation.mutate(event._id);
      },
    });
  };

  const handleViewDetails = (event: Event) => {
    setSelectedEvent(event);
    setIsModalOpen(true);
  };

  const isLoading = isLoadingHackathons || isLoadingEvents;
  const events = eventsResponse?.data || [];



  // Calculate pagination
  const totalItems = events.length;
  const totalPages = Math.ceil(totalItems / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedEvents = events.slice(startIndex, endIndex);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const columns = [
    {
      title: 'Image',
      dataIndex: 'image',
      key: 'image',
      width: 80,
      render: (image: string) => (
        <div className="w-12 h-12 rounded-lg overflow-hidden bg-gray-100 flex items-center justify-center">
          {image ? (
            <Image
              src={image}
              alt="Event"
              className="w-full h-full object-cover"
              fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAMIAAADDCAYAAADQvc6UAAABRWlDQ1BJQ0MgUHJvZmlsZQAAKJFjYGASSSwoyGFhYGDIzSspCnJ3UoiIjFJgf8LAwSDCIMogwMCcmFxc4BgQ4ANUwgCjUcG3awyMIPqyLsis7PPOq3QdDFcvjV3jOD1boQVTPQrgSkktTgbSf4A4LbmgqISBgTEFyFYuLykAsTuAbJEioKOA7DkgdjqEvQHEToKwj4DVhAQ5A9k3gGyB5IxEoBmML4BsnSQk8XQkNtReEOBxcfXxUQg1Mjc0dyHgXNJBSWpFCYh2zi+oLMpMzyhRcASGUqqCZ16yno6CkYGRAQMDKMwhqj/fAIcloxgHQqxAjIHBEugw5sUIsSQpBobtQPdLciLEVJYzMPBHMDBsayhILEqEO4DxG0txmrERhM29nYGBddr//5/DGRjYNRkY/l7////39v///y4Dmn+LgeHANwDrkl1AuO+pmgAAADhlWElmTU0AKgAAAAgAAYdpAAQAAAABAAAAGgAAAAAAAqACAAQAAAABAAAAwqADAAQAAAABAAAAwwAAAAD9b/HnAAAHlklEQVR4Ae3dP3Ik1RnG4W+FgYxN"
            />
          ) : (
            <div className="w-full h-full bg-gray-200 flex items-center justify-center text-gray-400">
              <EyeOutlined />
            </div>
          )}
        </div>
      ),
    },
    {
      title: "Event Details",
      dataIndex: "title",
      key: "title",
      render: (text: string, record: Event) => (
        <div>
          <a className="font-semibold text-gray-900 underline" target="_blank" rel="noopener noreferrer" href={record.eventLink}>{text}</a>
          <div className="text-sm text-gray-600">{record.description}</div>
        </div>
      ),
    },
    {
      title: "Date & Time",
      dataIndex: "date",
      key: "date",
      render: (date: string, record: Event) => (
        <div className="text-sm text-gray-700">
          {date} {record.time}
        </div>
      ),
    },
    {
      title: "Type",
      dataIndex: "eventType",
      key: "eventType",
      render: (type: string) => (
        <Tag color={type === 'virtual' ? 'blue' : 'green'} className="!text-xs !rounded-full !p-1 !px-2">
          {type === 'virtual' ? 'Virtual' : 'Physical'}
        </Tag>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_: any, record: Event) => (
        <Space size="middle">
          <Button
            type="text"
            icon={<EyeOutlined />}
            className="text-gray-500 hover:text-blue-600"
            onClick={() => handleViewDetails(record)}
          />
          <Button
            type="text"
            icon={<EditOutlined />}
            className="text-gray-500 hover:text-primary"
            onClick={() => handleEditEvent(record._id)}
          />
          <Button
            type="text"
            icon={<DeleteOutlined />}
            className="text-gray-500 hover:text-red-600"
            onClick={() => handleDeleteEvent(record)}
            loading={deleteEventMutation.isPending}
          />
        </Space>
      ),
    },
  ];

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-9">
        <h1 className="text-2xl font-semibold text-gray-900">Events</h1>
        <Button 
          type="primary" 
          className="bg-primary !hover:bg-blue-600/90 !h-[40px] !text-sm"
          icon={<PlusOutlined />}
          onClick={handleAddEvent}
        >
          Add Event
        </Button>
      </div>

      {isLoading ? (
        <Card>
          <Skeleton active paragraph={{ rows: 8 }} />
        </Card>
      ) : !activeHackathonId ? (
        <Card>
          <Empty
            description="No active hackathon found"
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        </Card>
      ) : (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          {events.length === 0 ? (
            <div className="p-8 text-center">
              <Empty
                description="No events found"
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              >
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={handleAddEvent}
                  className="bg-primary hover:bg-primary/90"
                >
                  Add First Event
                </Button>
              </Empty>
            </div>
          ) : (
            <>
              <Table
                columns={columns}
                dataSource={paginatedEvents}
                rowKey="_id"
                pagination={false}
                className="events-table"
              />
              
              {/* Custom Pagination */}
              {totalItems > pageSize && (
                <div className="mt-4 flex justify-center">
                  <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={handlePageChange}
                  />
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Event Details Modal */}
      <Modal
        title="Event Details"
        open={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
        footer={[
          <Button key="close" onClick={() => setIsModalOpen(false)}>
            Close
          </Button>,
        ]}
        width={600}
      >
        {selectedEvent && (
          <div className="space-y-4">
            {selectedEvent.image && (
              <div className="text-left">
                <Image
                  src={selectedEvent.image}
                  alt={selectedEvent.title}
                  className="max-w-full !h-[120px] object-cover rounded-lg"
                />
              </div>
            )}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                {selectedEvent.title}
              </h3>
              <p className="text-gray-600 mb-4">{selectedEvent.description}</p>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="font-medium text-gray-700">Type:</span>
                  <Tag color={selectedEvent.eventType === 'virtual' ? 'blue' : 'green'} className="ml-2">
                    {selectedEvent.eventType === 'virtual' ? 'Virtual' : 'Physical'}
                  </Tag>
                </div>
                <div>
                  <span className="font-medium text-gray-700">Date:</span>
                  <span className="ml-2 text-gray-600">{selectedEvent.date}</span>
                </div>
                <div>
                  <span className="font-medium text-gray-700">Time:</span>
                  <span className="ml-2 text-gray-600">{selectedEvent.time}</span>
                </div>
                {selectedEvent.eventLink && (
                  <div className="col-span-2">
                    <span className="font-medium text-gray-700">Link:</span>
                    <a
                      href={selectedEvent.eventLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="ml-2 text-blue-600 hover:text-blue-800"
                    >
                      {selectedEvent.eventLink}
                    </a>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default EventsList;
